/**
 * ImageUploadField – Wiederverwendbare Bild-Eingabe mit zwei Modi:
 *   • "URL eingeben"   – klassisches Text-Input
 *   • "Hochladen"      – Drag & Drop / Dateiauswahl (max. 5 MB)
 *
 * Props:
 *   value         – aktuelle URL (string)
 *   onChange      – (url: string) => void
 *   uploadEndpoint – z.B. "/media/artist-image"
 *   oldPendingUrl  – alte pending URL (wird beim neuen Upload gelöscht)
 *   label          – Feldbezeichnung
 *   testId         – data-testid prefix
 */
import React, { useState, useRef } from "react";
import { Upload, Link2, X, Loader2, ImageIcon, CheckCircle } from "lucide-react";
import api from "../utils/api";
import { usePublicAuth } from "../context/PublicAuthContext";

const MAX_MB_AVATAR = 5;
const MAX_MB_BANNER = 8;
const ALLOWED = ["image/jpeg", "image/png", "image/webp", "image/gif"];

export default function ImageUploadField({
  value = "",
  onChange,
  uploadEndpoint,
  oldPendingUrl = "",
  label = "Profilbild",
  testId = "image-upload",
  variant = "avatar",   // "avatar" | "banner"
  hint = "",            // optionaler Hinweistext unter dem Drop-Bereich
}) {
  const { token } = usePublicAuth();
  const [mode, setMode] = useState("url"); // "url" | "upload"
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [uploadDone, setUploadDone] = useState(false);
  const fileRef = useRef(null);

  const switchMode = (m) => {
    setMode(m);
    setUploadError("");
    setUploadDone(false);
  };

  const doUpload = async (file) => {
    setUploadError("");
    setUploadDone(false);

    const maxMb = variant === "banner" ? MAX_MB_BANNER : MAX_MB_AVATAR;
    const maxBytes = maxMb * 1024 * 1024;

    // Client-seitige Validierung
    if (!ALLOWED.includes(file.type)) {
      setUploadError("Ungültiger Dateityp. Erlaubt: JPEG, PNG, WebP, GIF");
      return;
    }
    if (file.size > maxBytes) {
      setUploadError(`Datei zu groß (${(file.size / 1024 / 1024).toFixed(1)} MB). Max. ${maxMb} MB`);
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      // Alte pending/live URL für Cleanup mitschicken
      if (oldPendingUrl) formData.append("old_pending_url", oldPendingUrl);
      if (!oldPendingUrl && value) formData.append("old_url", value);

      const res = await api.post(uploadEndpoint, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });
      const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
      const fullUrl = res.data.url.startsWith("http")
        ? res.data.url
        : `${backendUrl}${res.data.url}`;
      onChange(fullUrl);
      setUploadDone(true);
    } catch (e) {
      setUploadError(e?.response?.data?.detail || "Upload fehlgeschlagen");
    } finally {
      setUploading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) doUpload(file);
    e.target.value = "";
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) doUpload(file);
  };

  return (
    <div>
      {/* Label + Mode-Toggle */}
      <div className="flex items-center justify-between mb-2 ml-1">
        <label className="text-xs font-semibold uppercase tracking-[0.15em] text-zinc-500">
          {label}
        </label>
        <div className="flex items-center bg-zinc-800/60 rounded-lg p-0.5 gap-0.5">
          <button
            type="button"
            onClick={() => switchMode("url")}
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-semibold transition-all ${
              mode === "url"
                ? "bg-zinc-700 text-white shadow-sm"
                : "text-zinc-500 hover:text-zinc-300"
            }`}
            data-testid={`${testId}-mode-url`}
          >
            <Link2 size={10} /> URL
          </button>
          <button
            type="button"
            onClick={() => switchMode("upload")}
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-semibold transition-all ${
              mode === "upload"
                ? "bg-zinc-700 text-white shadow-sm"
                : "text-zinc-500 hover:text-zinc-300"
            }`}
            data-testid={`${testId}-mode-upload`}
          >
            <Upload size={10} /> Hochladen
          </button>
        </div>
      </div>

      {/* ── URL-Modus ── */}
      {mode === "url" && (
        <div>
          <input
            value={value}
            onChange={e => onChange(e.target.value)}
            placeholder="https://..."
            className="w-full bg-zinc-900/50 text-white rounded-2xl px-4 py-3 text-sm focus:outline-none ring-1 ring-inset ring-white/[0.07] focus:ring-rose-500/40 transition-all duration-200 placeholder:text-zinc-700"
            data-testid={`${testId}-url-input`}
          />
        </div>
      )}

      {/* ── Upload-Modus ── */}
      {mode === "upload" && (
        <div>
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            className="hidden"
            onChange={handleFileChange}
            data-testid={`${testId}-file-input`}
          />
          <div
            onDragOver={e => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={handleDrop}
            onClick={() => !uploading && fileRef.current?.click()}
            className={`relative w-full rounded-2xl border-2 border-dashed transition-all duration-200 cursor-pointer overflow-hidden
              ${dragging ? "border-rose-500/60 bg-rose-500/5" : "border-white/10 bg-zinc-900/40 hover:border-white/20 hover:bg-zinc-900/60"}
              ${uploading ? "cursor-default" : ""}
            `}
            style={{ minHeight: variant === "banner" ? "80px" : "100px" }}
            data-testid={`${testId}-drop-zone`}
          >
            {uploading ? (
              <div className="flex flex-col items-center justify-center gap-2 py-6">
                <Loader2 size={24} className="text-zinc-400 animate-spin" />
                <p className="text-zinc-500 text-xs">Wird hochgeladen…</p>
              </div>
            ) : uploadDone ? (
              <div className="flex flex-col items-center justify-center gap-2 py-6">
                <CheckCircle size={24} className="text-emerald-400" />
                <p className="text-emerald-400 text-xs font-semibold">Bild hochgeladen!</p>
                <p className="text-zinc-600 text-[11px]">Klicken um ein anderes zu wählen</p>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center gap-2 py-5 select-none">
                <ImageIcon size={22} className="text-zinc-600" />
                <p className="text-zinc-400 text-sm font-medium">Bild hierher ziehen</p>
                <p className="text-zinc-600 text-xs">oder klicken zum Auswählen</p>
                <span className="mt-0.5 text-zinc-700 text-[11px]">
                  {variant === "banner"
                    ? "JPEG · PNG · WebP · max. 8 MB · Empfehlung: breit & flach (z.B. 1400×400px)"
                    : "JPEG · PNG · WebP · GIF · max. 5 MB"}
                </span>
              </div>
            )}
          </div>
          {uploadError && (
            <div className="flex items-start gap-1.5 mt-2 text-red-400 text-xs">
              <X size={12} className="flex-shrink-0 mt-0.5" /> {uploadError}
            </div>
          )}
        </div>
      )}

      {/* ── Vorschau (immer sichtbar wenn URL gesetzt) ── */}
      {value && (
        variant === "banner" ? (
          <div className="mt-3 relative group">
            <img
              src={value}
              alt="Banner Vorschau"
              className="w-full rounded-xl object-cover ring-1 ring-white/10"
              style={{ height: "110px", objectPosition: "center center" }}
              onError={e => { e.target.style.display = "none"; }}
            />
            <button
              type="button"
              onClick={() => onChange("")}
              className="absolute top-2 right-2 bg-black/60 hover:bg-red-500/80 text-white rounded-lg p-1.5 transition-colors opacity-0 group-hover:opacity-100"
              data-testid={`${testId}-clear`}
              title="Banner entfernen"
            >
              <X size={12} />
            </button>
          </div>
        ) : (
          <div className="mt-3 flex items-center gap-3">
            <img
              src={value}
              alt="Vorschau"
              className="w-12 h-12 rounded-xl object-cover ring-1 ring-white/10 flex-shrink-0"
              onError={e => { e.target.style.display = "none"; }}
            />
            <div className="flex-1 min-w-0">
              <p className="text-zinc-600 text-[11px] truncate">{value}</p>
              <button
                type="button"
                onClick={() => onChange("")}
                className="text-zinc-700 hover:text-red-400 text-[11px] transition-colors mt-0.5"
                data-testid={`${testId}-clear`}
              >
                Bild entfernen
              </button>
            </div>
          </div>
        )
      )}
    </div>
  );
}
